import React, { useState, useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Tabs, Tab, Grid, Paper, Typography, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getStyleVariables, menuLightGrey } from 'styles/vars';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import GVSelect from 'components/lib/GVSelect/GVSelect';
import { parseIniToBarcodeProfileFields } from 'pages/MyAccount/customProfiles/profileFormatter';
import { BarcodeProfileFields, EditorChildProps, defaultBarcodeFields } from './types';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '520px',
    },
    contentPaper: {
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '4px',
      padding: theme.spacing(2, 3),
      marginTop: theme.spacing(1),
      backgroundColor: menuLightGrey,
    },
    formArea: {
      flex: 1,
      minWidth: 0,
      width: '100%',
      overflow: 'auto',
      boxSizing: 'border-box',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(0.5),
      ...styleVariables.scrollBarThin,
      scrollbarGutter: 'auto',
    },
    gridContainer: {
      width: '100%',
    },
    sectionSubtitle: {
      color: '#ffffff',
      marginBottom: theme.spacing(1),
      fontSize: '0.875rem',
    },
    tabs: {
      width: '100%',
      '& .MuiTabs-flexContainer': {
        width: '100%',
        justifyContent: 'space-between',
      },
      '& .MuiTab-root': {
        flexShrink: 0,
        minWidth: 'min-content',
        maxWidth: 'none',
        padding: theme.spacing(1.5, 1.75),
        fontSize: '0.8rem',
        textTransform: 'none',
        whiteSpace: 'nowrap',
      },
    },
  };
});

function isNumericError(value: string, min: number, max?: number): boolean {
  if (value === '' || value == null) return true;
  const n = Number(value);
  if (isNaN(n)) return true;
  if (n < min) return true;
  if (max !== undefined && n > max) return true;
  return false;
}

function computeHasErrors(f: BarcodeProfileFields): boolean {
  return !!(
    (f.rasterDPI && isNumericError(f.rasterDPI, 100, 600)) ||
    (f.barcodeReRasterDpi && isNumericError(f.barcodeReRasterDpi, 300, 2400)) ||
    isNumericError(f.Linear_ScanGapHor, 0, 32) ||
    isNumericError(f.Linear_ScanGapVer, 0, 32) ||
    isNumericError(f.Linear_MinScore, 0, 100) ||
    isNumericError(f.Linear_MinPharmacodeWidth, 4, 1000) ||
    isNumericError(f.Linear_MinPharmacodeHeight, 4, 1000) ||
    isNumericError(f.Linear_MinPharmacodeBars, 2, 16) ||
    isNumericError(f.Linear_MinVicinityHor, 1, 1000) ||
    isNumericError(f.Linear_MinVicinityVer, 1, 1000) ||
    isNumericError(f.Linear_EdgeThresh, 1, 100) ||
    isNumericError(f.Linear_MaxPharmacodeWidth, 4, 1000) ||
    isNumericError(f.Linear_MaxPharmacodeHeight, 4, 1000) ||
    isNumericError(f.Linear_MinBWR, -1, 1) ||
    isNumericError(f.Linear_MaxBWR, -1, 1) ||
    isNumericError(f.DataMatrix_MaxExpectedSymbols, 1, 100) ||
    isNumericError(f.DataMatrix_ScanGap, 0, 32) ||
    isNumericError(f.DataMatrix_LocatorEffortLevel, 0, 10) ||
    isNumericError(f.DataMatrix_EdgeThresh, 1, 100) ||
    isNumericError(f.DataMatrix_MinModuleSize, 1, 100) ||
    isNumericError(f.DataMatrix_MaxSymbolSize, 1, 2000) ||
    isNumericError(f.DataMatrix_MaxSkewAngle, 0, 90) ||
    isNumericError(f.QR_ScanGapHor, 0, 32) ||
    isNumericError(f.QR_ScanGapVer, 0, 32) ||
    isNumericError(f.QR_MinScore, 0, 100) ||
    isNumericError(f.QR_EdgeThresh, 1, 100) ||
    isNumericError(f.General_SmoothFilter, 0, 6) ||
    isNumericError(f.General_MedianFilter, 0, 6) ||
    isNumericError(f.General_OpenFilter, 0, 6) ||
    isNumericError(f.General_CloseFilter, 0, 6) ||
    isNumericError(f.General_OutputFormatGS1, 0, 2)
  );
}

type BarcodeEditorProps = EditorChildProps<BarcodeProfileFields>;

const BarcodeProfileEditor: React.FC<BarcodeEditorProps> = ({ profile, onFieldsChange, onValidChange }) => {
  const { classes } = useStyles();
  const featureFlags = useFlags();
  const showBarcodeHrQrContent = featureFlags.showBarcodeHrQrContent === true;
  const disableBarcodeHrQrAi = !showBarcodeHrQrContent;

  const [tab, setTab] = useState(0);
  const [fields, setFields] = useState<BarcodeProfileFields>(defaultBarcodeFields);
  const [focusedField, setFocusedField] = useState<keyof BarcodeProfileFields | null>(null);

  useEffect(() => {
    if (profile?.customProfileString) {
      const parsed = parseIniToBarcodeProfileFields(profile.customProfileString);
      setFields({
        ...parsed,
        rasterDPI: profile.rasterDPI ? String(profile.rasterDPI) : undefined,
        barcodeReRasterDpi: profile.barcodeReRasterDpi ? String(profile.barcodeReRasterDpi) : undefined,
      });
    } else {
      setFields(defaultBarcodeFields);
    }
    setTab(0);
  }, [profile]);

  useEffect(() => {
    if (!disableBarcodeHrQrAi) return;
    setFields((prev) => {
      if (!prev.Linear_HumanReadable && !prev.QR_ResolvedUrl) return prev;
      return { ...prev, Linear_HumanReadable: false, QR_ResolvedUrl: false };
    });
  }, [disableBarcodeHrQrAi, profile]);

  useEffect(() => {
    onFieldsChange(fields);
    onValidChange(computeHasErrors(fields));
  }, [fields, onFieldsChange, onValidChange]);

  const set = (key: keyof BarcodeProfileFields, val: string | boolean | undefined) =>
    setFields((prev) => ({ ...prev, [key]: val }));

  const blurToDefault = (key: keyof BarcodeProfileFields, def: string) =>
    setFields((prev) => ({
      ...prev,
      [key]: (prev[key] as string).trim() === '' ? def : prev[key],
    }));

  const focus = (key: keyof BarcodeProfileFields) => () => setFocusedField(key);
  const blur = (key: keyof BarcodeProfileFields, def?: string) => () => {
    setFocusedField(null);
    if (def !== undefined) blurToDefault(key, def);
  };

  const helper = (key: keyof BarcodeProfileFields, text: string) => (focusedField === key ? text : undefined);

  const numField = (
    key: keyof BarcodeProfileFields,
    label: string,
    min: number,
    max: number,
    def: string,
    helperText: string,
    testId: string,
    xs?: 4 | 6 | 12,
  ) => (
    <Grid item xs={xs ?? 6}>
      <GVTextField
        label={label}
        value={(fields[key] as string) ?? ''}
        onValueChange={(value) => set(key, value ?? '')}
        onFocus={focus(key)}
        onBlur={blur(key, def)}
        message={helper(key, helperText)}
        hasError={isNumericError(fields[key] as string, min, max)}
        data-testid={testId}
      />
    </Grid>
  );

  const optionalNumField = (
    key: 'rasterDPI' | 'barcodeReRasterDpi',
    label: string,
    min: number,
    max: number,
    helperText: string,
    testId: string,
  ) => (
    <Grid item xs={6}>
      <GVTextField
        label={label}
        value={fields[key] ?? ''}
        onValueChange={(value) => set(key, value || undefined)}
        onFocus={focus(key)}
        onBlur={blur(key)}
        message={helper(key, helperText)}
        hasError={fields[key] ? isNumericError(fields[key] as string, min, max) : false}
        data-testid={testId}
      />
    </Grid>
  );

  const checkField = (
    key: keyof BarcodeProfileFields,
    label: string,
    testId: string,
    xs?: 4 | 6 | 12,
    inverted?: boolean,
    disabled?: boolean,
  ) => (
    <Grid item xs={xs ?? 6}>
      <GVCheckbox
        checked={inverted ? !fields[key] : !!(fields[key] as boolean)}
        onChange={(e) => set(key, inverted ? !e.target.checked : e.target.checked)}
        label={label}
        data-testid={testId}
        disabled={disabled}
      />
    </Grid>
  );

  return (
    <div className={classes.root}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        textColor="inherit"
        indicatorColor="secondary"
        className={classes.tabs}
      >
        <Tab label="Linear Barcode" />
        <Tab label="Linear Barcode Types" />
        <Tab label="Data Matrix" />
        <Tab label="QR Code" />
        <Tab label="Options" />
        <Tab label="Image Settings (Optional)" />
      </Tabs>

      <Paper elevation={0} className={classes.contentPaper}>
        <div className={classes.formArea}>
          {/* Linear Barcode */}
          {tab === 0 && (
            <Grid container spacing={1} className={classes.gridContainer}>
              {checkField('Linear_IgnoreBarCode', 'Process', 'linear-barcode-process', 12, true)}
              {numField(
                'Linear_ScanGapHor',
                'Scan gap horizontal (pixel columns)',
                0,
                32,
                '2',
                'Range: 0–32, Default: 2',
                'scan-gap-horizontal-pixel-columns',
              )}
              {numField(
                'Linear_ScanGapVer',
                'Scan gap vertical (pixel rows)',
                0,
                32,
                '2',
                'Range: 0–32, Default: 2',
                'scan-gap-vertical-pixel-rows',
              )}
              {numField(
                'Linear_MinScore',
                'Minimum score',
                0,
                100,
                '5',
                'Range: 0–100, Default: 5',
                'minimum-score-linear',
              )}
              {numField(
                'Linear_EdgeThresh',
                'Edge threshold',
                1,
                100,
                '40',
                'Range: 1–100, Default: 40',
                'edge-threshold-linear',
              )}
              {numField(
                'Linear_MinPharmacodeWidth',
                'Min. pharma width (pixels)',
                4,
                1000,
                '20',
                'Range: 4–1000, Default: 20',
                'min-pharma-width-pixels',
                4,
              )}
              {numField(
                'Linear_MinPharmacodeHeight',
                'Min. pharma height (pixels)',
                4,
                1000,
                '20',
                'Range: 4–1000, Default: 20',
                'min-pharma-height-pixels',
                4,
              )}
              {numField(
                'Linear_MinPharmacodeBars',
                'Min. pharma bars (count)',
                2,
                16,
                '4',
                'Range: 2–16, Default: 4',
                'min-pharma-bars-count',
                4,
              )}
              {numField(
                'Linear_MaxPharmacodeWidth',
                'Max. pharma width (pixels)',
                4,
                1000,
                '600',
                'Range: 4–1000, Default: 600',
                'max-pharma-width-pixels',
              )}
              {numField(
                'Linear_MaxPharmacodeHeight',
                'Max. pharma height (pixels)',
                4,
                1000,
                '200',
                'Range: 4–1000, Default: 200',
                'max-pharma-height-pixels',
              )}
              {numField(
                'Linear_MinVicinityHor',
                'Min. vicinity horizontal (pixels)',
                1,
                1000,
                '64',
                'Range: 1–1000, Default: 64',
                'minimum-vicinity-horizontal-pixels',
              )}
              {numField(
                'Linear_MinVicinityVer',
                'Min. vicinity vertical (pixels)',
                1,
                1000,
                '76',
                'Range: 1–1000, Default: 76',
                'minimum-vicinity-vertical-pixels',
              )}
              {numField('Linear_MinBWR', 'Min. BWR (mm)', -1, 1, '-0.1', 'Range: −1 to 1, Default: −0.1', 'min-bwr-mm')}
              {numField('Linear_MaxBWR', 'Max. BWR (mm)', -1, 1, '0.1', 'Range: −1 to 1, Default: 0.1', 'max-bwr-mm')}
              {checkField(
                'Linear_FailOutOfRangeBWR',
                'Fail grading if BWR out of range',
                'fail-grading-if-bwr-out-of-range',
              )}
              {checkField('Linear_ISO_IEC_15416_2016', 'ISO/IEC 2016 standard', 'iso-iec-2016-standard')}
              {checkField('Linear_EnableRotation', 'Support rotation', 'support-rotation')}
              {checkField('Linear_BackgroundColor', 'Support negative barcodes', 'support-negative-barcodes')}
              {checkField('Linear_IgnoreChecksum', 'Ignore checksum', 'ignore-checksum')}
              {checkField(
                'Linear_HumanReadable',
                'Human readable',
                'human-readable',
                undefined,
                undefined,
                disableBarcodeHrQrAi,
              )}
              {checkField(
                'Linear_GradeDataStructure',
                'Include in grading: Data structure',
                'include-in-grading-data-structure',
              )}
              {checkField('Linear_GradeQuietZone', 'Include in grading: Quiet zone', 'include-in-grading-quiet-zone')}
              {checkField('Linear_GradeNarrowBar', 'Include in grading: Narrow bar', 'include-in-grading-narrow-bar')}
              {checkField(
                'Linear_GradeWideToNarrowRatio',
                'Include in grading: Wide to narrow ratio',
                'include-in-grading-wide-to-narrow-ratio',
              )}
              {checkField(
                'Linear_UspsMeasurementNominal',
                'Nominal measurements for USPS 4-State',
                'nominal-measurements-for-usps-4-state',
              )}
            </Grid>
          )}

          {/* Linear Barcode Types */}
          {tab === 1 && (
            <Grid container spacing={1} className={classes.gridContainer}>
              <Grid item xs={12}>
                <Typography className={classes.sectionSubtitle}>Select the barcode types to ignore</Typography>
              </Grid>
              {checkField('IgnoreBarCodeType_UPCA', 'UPC-A', 'upc-a', 4)}
              {checkField('IgnoreBarCodeType_UPCE', 'UPC-E', 'upc-e', 4)}
              {checkField('IgnoreBarCodeType_EAN2', 'EAN-2', 'ean-2', 4)}
              {checkField('IgnoreBarCodeType_EAN5', 'EAN-5', 'ean-5', 4)}
              {checkField('IgnoreBarCodeType_EAN8', 'EAN-8', 'ean-8', 4)}
              {checkField('IgnoreBarCodeType_EAN13', 'EAN-13', 'ean-13', 4)}
              {checkField('IgnoreBarCodeType_I25', 'Interleaved 2 of 5', 'interleaved-2-of-5', 4)}
              {checkField('IgnoreBarCodeType_DATABAR', 'GS1 DataBar', 'gs1-databar', 4)}
              {checkField('IgnoreBarCodeType_DATABAR_EXP', 'GS1 DataBar Expanded', 'gs1-databar-expanded', 4)}
              {checkField('IgnoreBarCodeType_DATABAR_LTD', 'GS1 DataBar Limited', 'gs1-databar-limited', 4)}
              {checkField('IgnoreBarCodeType_CODABAR', 'Codabar', 'codabar', 4)}
              {checkField('IgnoreBarCodeType_CODE11', 'Code 11', 'code-11', 4)}
              {checkField('IgnoreBarCodeType_CODE32', 'Code 32', 'code-32', 4)}
              {checkField('IgnoreBarCodeType_CODE39', 'Code 39', 'code-39', 4)}
              {checkField('IgnoreBarCodeType_CODE93', 'Code 93', 'code-93', 4)}
              {checkField('IgnoreBarCodeType_CODE128', 'Code 128 / GS1 128', 'code-128-gs1-128', 4)}
              {checkField('IgnoreBarCodeType_MSI', 'MSI', 'msi', 4)}
              {checkField('IgnoreBarCodeType_TELEPEN', 'Telepen', 'telepen', 4)}
              {checkField('IgnoreBarCodeType_PHARMACODE', 'Laetus Pharma Code', 'laetus-pharma-code', 4)}
              {checkField('IgnoreBarCodeType_USPS_4STATE', 'USPS 4-State', 'usps-4-state', 4)}
            </Grid>
          )}

          {/* Data Matrix */}
          {tab === 2 && (
            <Grid container spacing={1} className={classes.gridContainer}>
              {checkField('DataMatrix_IgnoreDmtxCode', 'Process', 'data-matrix-process', 12, true)}
              {numField(
                'DataMatrix_MaxExpectedSymbols',
                'Max. expected symbols',
                1,
                100,
                '3',
                'Range: 1–100, Default: 3',
                'max-expected-symbols',
              )}
              {numField(
                'DataMatrix_ScanGap',
                'Scan gap (pixel columns)',
                0,
                32,
                '8',
                'Range: 0–32, Default: 8',
                'scan-gap-pixel-columns-data-matrix',
              )}
              {numField(
                'DataMatrix_LocatorEffortLevel',
                'Locator effort level',
                0,
                10,
                '1',
                'Range: 0–10, Default: 1',
                'locator-effort-level',
              )}
              {numField(
                'DataMatrix_EdgeThresh',
                'Edge threshold',
                1,
                100,
                '10',
                'Range: 1–100, Default: 10',
                'edge-threshold-data-matrix',
              )}
              {numField(
                'DataMatrix_MinModuleSize',
                'Min. module size (pixels)',
                1,
                100,
                '4',
                'Range: 1–100, Default: 4',
                'min-module-size-pixels',
                4,
              )}
              {numField(
                'DataMatrix_MaxSymbolSize',
                'Max. symbol size (pixels)',
                1,
                2000,
                '600',
                'Range: 1–2000, Default: 600',
                'max-symbol-size-pixels',
                4,
              )}
              {numField(
                'DataMatrix_MaxSkewAngle',
                'Max. skew angle (degrees)',
                0,
                90,
                '15',
                'Range: 0–90, Default: 15',
                'max-skew-angle-degrees',
                4,
              )}
              {checkField(
                'DataMatrix_GradeNonISOParams',
                'Grade NON ISO parameters',
                'grade-non-iso-parameters-data-matrix',
              )}
              {checkField(
                'DataMatrix_EnableMirrored',
                'Support mirrored barcodes',
                'support-mirrored-barcodes-data-matrix',
              )}
            </Grid>
          )}

          {/* QR Code */}
          {tab === 3 && (
            <Grid container spacing={1} className={classes.gridContainer}>
              {checkField('QR_IgnoreQrCode', 'Process', 'qr-code-process', 12, true)}
              {numField(
                'QR_ScanGapHor',
                'Scan gap horizontal (pixel columns)',
                0,
                32,
                '1',
                'Range: 0–32, Default: 1',
                'scan-gap-hor-pixel-columns',
              )}
              {numField(
                'QR_ScanGapVer',
                'Scan gap vertical (pixel rows)',
                0,
                32,
                '1',
                'Range: 0–32, Default: 1',
                'scan-gap-ver-pixel-rows',
              )}
              {numField('QR_MinScore', 'Minimum score', 0, 100, '5', 'Range: 0–100, Default: 5', 'minimum-score-qr')}
              {numField(
                'QR_EdgeThresh',
                'Edge threshold',
                1,
                100,
                '40',
                'Range: 1–100, Default: 40',
                'edge-threshold-qr',
              )}
              {checkField('QR_GradeNonISOParams', 'Grade NON ISO parameters', 'grade-non-iso-parameters-qr')}
              {checkField('QR_EnableMirrored', 'Support mirrored barcodes', 'support-mirrored-barcodes-qr')}
              {checkField('QR_ResolvedUrl', 'Resolved URL', 'resolved-url', undefined, undefined, disableBarcodeHrQrAi)}
            </Grid>
          )}

          {/* Options */}
          {tab === 4 && (
            <Grid container spacing={1} className={classes.gridContainer}>
              {checkField(
                'General_RedLightSimulation',
                'Enable red light simulation',
                'enable-red-light-simulation',
                12,
              )}
              {numField('General_SmoothFilter', 'Smooth filter', 0, 6, '0', 'Range: 0–6, Default: 0', 'smooth-filter')}
              {numField('General_MedianFilter', 'Median filter', 0, 6, '0', 'Range: 0–6, Default: 0', 'median-filter')}
              {numField('General_OpenFilter', 'Open filter', 0, 6, '0', 'Range: 0–6, Default: 0', 'open-filter')}
              {numField('General_CloseFilter', 'Close filter', 0, 6, '0', 'Range: 0–6, Default: 0', 'close-filter')}
              {numField(
                'General_OutputFormatGS1',
                'GS1 output data format',
                0,
                2,
                '0',
                'Range: 0–2, Default: 0',
                'gs1-output-data-format',
              )}
              <Grid item xs={6}>
                <GVSelect
                  id="grade-output-format"
                  label="Grade output format"
                  value={fields.General_OutputFormatGrade ?? '0'}
                  onValueChange={(nextValue) => set('General_OutputFormatGrade', nextValue)}
                  options={[
                    { value: '0', label: 'Letters' },
                    { value: '1', label: 'Numbers' },
                  ]}
                />
              </Grid>
            </Grid>
          )}

          {/* Image Settings */}
          {tab === 5 && (
            <Grid container spacing={1} className={classes.gridContainer}>
              {optionalNumField('rasterDPI', 'Raster DPI', 100, 600, 'Range: 100–600 (optional)', 'raster-dpi')}
              {optionalNumField(
                'barcodeReRasterDpi',
                'Barcode Re-Raster DPI',
                300,
                2400,
                'Range: 300–2400 (optional)',
                'barcode-re-raster-dpi',
              )}
            </Grid>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default BarcodeProfileEditor;
